// Pantalla Modelia (`/credit/modelia`): el único componente con estado del módulo. Ensambla el
// explorer (filtros · lista · ficha) y los diálogos de ayuda y del modelo.
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { MasterDetailExplorer } from '@/shared/patterns/MasterDetail';
import {
    EVALUATIONS_NOUN,
    LoadedScopeFooter,
    loadedFiltersNote,
    type LoadedScope,
} from '@/shared/patterns/LoadedScope';
import { ScreenControls } from '@/shared/patterns/AppShell';
import { ScreenFrame, ScreenFrameBody } from '@/shared/patterns/ScreenFrame';
import { useAuth } from '@/hooks/useAuth';
import { useModeliaEvaluations, useRerunSciEvaluation } from './api/useModeliaEvaluations';
import {
    anyRowHasPhone,
    describeCoverageGaps,
    describeRerunFailure,
    emptyEvaluationFilters,
    EMPTY_EVALUATIONS_NOTE,
    resolveReadState,
} from './logic';
import { MODELIA_FORBIDDEN_DESCRIPTION, resolveModeliaAbilities } from './permissions';
import type { ModeliaEvaluationFilters } from './types';
import {
    ModeliaEvaluationDetail,
    type ModeliaDetailTab,
} from './components/ModeliaEvaluationDetail';
import {
    ModeliaEvaluationsTable,
    useModeliaEvaluationsTable,
} from './components/ModeliaEvaluationsTable';
import { ModeliaFiltersPanel } from './components/ModeliaFiltersPanel';
import { ModeliaHelpDialog } from './components/ModeliaHelpDialog';
import { ModeliaModelDialog } from './components/ModeliaModelDialog';
import { ModeliaForbiddenBlock, ModeliaReadStateBlock } from './components/ModeliaReadState';
import { ModeliaCoverageNotice } from './components/ModeliaCoverageNotice';
import { ModeliaRerunAction, RERUN_SUCCESS_NOTE } from './components/ModeliaRerunAction';

export function ModeliaPage() {
    const { permissions } = useAuth();
    const abilities = useMemo(() => resolveModeliaAbilities(permissions), [permissions]);

    const [filters, setFilters] = useState<ModeliaEvaluationFilters>(emptyEvaluationFilters);
    const [filtersCollapsed, setFiltersCollapsed] = useState(false);
    const [selectedEvaluationId, setSelectedEvaluationId] = useState<string | null>(null);
    const [detailTab, setDetailTab] = useState<ModeliaDetailTab>('panel');

    const evaluations = useModeliaEvaluations();
    const { rows, coverage } = evaluations;
    const table = useModeliaEvaluationsTable(rows, filters);
    const { rerun, isRunning } = useRerunSciEvaluation();

    // Declarada como función y no con `useCallback` porque el «Reintentar» del toast se llama a sí
    // misma: el 502 del padrón es transitorio y ahí sí vale insistir.
    function runRerun(evaluationId: string): void {
        void rerun(evaluationId).then(
            (created) => {
                setSelectedEvaluationId(created.evaluationId);
                table.setPageIndex(0);
                toast.success('Listo, el análisis se volvió a correr', {
                    description: RERUN_SUCCESS_NOTE,
                });
            },
            (error: unknown) => {
                const failure = describeRerunFailure(error);
                if (failure.refreshList) evaluations.refetch();
                toast.error('No se pudo volver a correr el análisis', {
                    description: failure.message,
                    action: failure.retryable
                        ? {
                              label: 'Reintentar',
                              onClick: () => {
                                  runRerun(evaluationId);
                              },
                          }
                        : undefined,
                });
            },
        );
    }

    const evaluationsState = resolveReadState({
        isLoading: evaluations.isLoading,
        error: evaluations.error,
        count: rows.length,
    });

    const coverageGaps = useMemo(() => describeCoverageGaps(coverage), [coverage]);
    const scope: LoadedScope = { loaded: coverage.loadedCount, hasMore: coverage.hasMore };
    const filtersNote = loadedFiltersNote(scope, EVALUATIONS_NOUN);
    // Cuenta sobre TODO lo cargado, no sobre la página visible: la página no es una medida de nada.
    const filteredCount = table.getFilteredRowModel().rows.length;
    const phoneSearchable = useMemo(() => anyRowHasPhone(rows), [rows]);

    // Sin selección explícita abre en la primera de la página: la más reciente que pasa el filtro.
    const pageRows = table.getRowModel().rows;
    const openId = selectedEvaluationId ?? pageRows.at(0)?.original.evaluation.evaluationId ?? null;
    const openRow = useMemo(
        () => rows.find((row) => row.evaluation.evaluationId === openId) ?? null,
        [rows, openId],
    );

    const handleFiltersChange = useCallback((next: ModeliaEvaluationFilters) => {
        setFilters(next);
        setSelectedEvaluationId(null);
    }, []);

    if (!abilities.canViewEvaluations) {
        return (
            <ModeliaForbiddenBlock description={MODELIA_FORBIDDEN_DESCRIPTION} className="m-4" />
        );
    }

    return (
        <ScreenFrame>
            {/* Los controles van al topbar, pegados al título: la pantalla no gasta una fila propia
                en ellos y el contenido arranca pegado al encabezado. */}
            <ScreenControls>
                <ModeliaModelDialog canViewParameters={abilities.canViewParameters} />
                <ModeliaHelpDialog />
            </ScreenControls>

            {evaluationsState === 'ready' ? (
                <>
                    <ModeliaCoverageNotice
                        title="Esta lista tiene filas sin nombre de cliente."
                        gaps={coverageGaps}
                    />
                    <ScreenFrameBody className="p-0">
                        <MasterDetailExplorer
                            filtersCollapsed={filtersCollapsed}
                            filters={
                                <ModeliaFiltersPanel
                                    filters={filters}
                                    onChange={handleFiltersChange}
                                    loadedNote={filtersNote}
                                    phoneSearchable={phoneSearchable}
                                    collapsed={filtersCollapsed}
                                    onToggleCollapsed={() => {
                                        setFiltersCollapsed((prev) => !prev);
                                    }}
                                />
                            }
                            list={
                                <div className="flex min-h-0 flex-1 flex-col">
                                    <ModeliaEvaluationsTable
                                        table={table}
                                        selectedEvaluationId={openId}
                                        onSelect={setSelectedEvaluationId}
                                    />
                                    <LoadedScopeFooter
                                        noun={EVALUATIONS_NOUN}
                                        scope={scope}
                                        shown={filteredCount}
                                        table={table}
                                        hasNextPage={evaluations.hasNextPage}
                                        isFetchingNextPage={evaluations.isFetchingNextPage}
                                        onLoadMore={evaluations.fetchNextPage}
                                        nextPageError={evaluations.nextPageError}
                                    />
                                </div>
                            }
                            detail={
                                <ModeliaEvaluationDetail
                                    row={openRow}
                                    tab={detailTab}
                                    onTabChange={setDetailTab}
                                    actions={
                                        openRow === null ? undefined : (
                                            <ModeliaRerunAction
                                                row={openRow}
                                                granted={abilities.canRerunEvaluations}
                                                isRunning={isRunning}
                                                onConfirm={runRerun}
                                            />
                                        )
                                    }
                                />
                            }
                        />
                    </ScreenFrameBody>
                </>
            ) : (
                <ScreenFrameBody className="p-4">
                    <ModeliaReadStateBlock
                        state={evaluationsState}
                        loadingText="Cargando las evaluaciones del motor…"
                        emptyTitle="No hay evaluaciones que mostrar"
                        emptyDescription={EMPTY_EVALUATIONS_NOTE}
                        errorTitle="No se pudieron leer las evaluaciones"
                        error={evaluations.error}
                        onRetry={evaluations.refetch}
                        forbiddenDescription={MODELIA_FORBIDDEN_DESCRIPTION}
                    />
                </ScreenFrameBody>
            )}
        </ScreenFrame>
    );
}
